import React, { useEffect, useState } from "react";
import { COMMENT_BLOCK_TYPE, Track } from "@highnote/server/src/core/entities";
import { highnote } from "@highnote/server/src/sdk";
import { EntityRowConfig, ENTITY_TYPE, Version } from "./config";
import { ReactComponent as ChatNewSvg } from "App/common/icons-v2/chat-new-line.svg";
import { ReactComponent as ChatSvg } from "App/common/icons-v2/chat-line.svg";
import { useHistory } from "react-router";
import { Button, BUTTON_THEME } from "App/core/Button";
import { RenderableTrack } from "App/store/tracks/buildSpaceTrackTile";

interface TrackCommentCountProps {
  entity: Track | Version;
  entityType: ENTITY_TYPE;
  /** Seed shown until `fetchCommentCount` answers (or when it never runs). */
  initialCommentCount?: number;
  /** Absent when there is no space to count comments under. */
  fetchCommentCount?: () => Promise<number>;
  /** Returns undefined when there is no space to navigate to. */
  generateUrl: (entity: Track | Version) => string | undefined;
}

const TrackCommentCount = ({
  entity,
  initialCommentCount,
  fetchCommentCount,
  generateUrl,
}: TrackCommentCountProps) => {
  const [commentCount, setCommentCount] = useState<number>(
    (entity as Version).commentCount ?? initialCommentCount ?? 0,
  );
  const history = useHistory();

  useEffect(() => {
    let unmounted = false;

    if (!fetchCommentCount) {
      return;
    }

    fetchCommentCount().then((count) => {
      if (unmounted) return;
      setCommentCount(count);
    });

    return () => {
      unmounted = true;
    };
  }, [entity]);

  useEffect(() => {
    if ("commentCount" in entity && entity.commentCount !== undefined) {
      setCommentCount(entity.commentCount);
    }
  }, [(entity as Version).commentCount]);

  const commentCountStr = commentCount >= 99 ? "99+" : `${commentCount}`;

  return (
    <Button
      className="highnote-entity-comments"
      theme={BUTTON_THEME.ICON}
      type="button"
      onClick={(e) => {
        // Prevent the click event from propagating to the parent element
        e.preventDefault();
        e.stopPropagation();
        const url = generateUrl(entity);
        if (url) {
          history.push(url);
        }
      }}
    >
      {commentCount > 0 ? <ChatSvg /> : <ChatNewSvg />}
      {commentCount > 0 && (
        <span className="comment-count">{commentCountStr}</span>
      )}
    </Button>
  );
};

export const EntityComments = ({ row }: { row: EntityRowConfig }) => {
  if (row.type === ENTITY_TYPE.TRACK) {
    const track = row.entity as RenderableTrack;
    // Comments are per (spaceId, trackId): a borrowed track's thread, and
    // its badge, belong to the space the row is VIEWED under, never to
    // `track.spaceId` (the origin — a space the viewer often cannot read,
    // and absent altogether on a row-only tile, which is where
    // `/space/undefined/...` deeplinks came from). A plain `Track` with no
    // viewing space (Search, Library) falls back to the origin, as before.
    const spaceId = row.viewingSpaceId ?? track.spaceId;
    return (
      <TrackCommentCount
        entity={track as Track}
        entityType={ENTITY_TYPE.TRACK}
        // The row's own per-space summary is the right seed: it IS this
        // space's thread count, mirrored server-side.
        initialCommentCount={track.rowSummary?.commentCount}
        fetchCommentCount={
          spaceId
            ? () =>
                highnote.getCommentsCount({
                  spaceId,
                  trackId: track.id,
                  excludeCommentTypes: [
                    COMMENT_BLOCK_TYPE.POLL_REPLY,
                    COMMENT_BLOCK_TYPE.REACTION,
                  ],
                })
            : undefined
        }
        generateUrl={(entity: Track) =>
          spaceId ? `/space/${spaceId}/${entity.id}` : undefined
        }
      />
    );
  }

  if (row.type === ENTITY_TYPE.TRACK_VERSION) {
    const trackVersion = row.entity as Version;
    return (
      <TrackCommentCount
        entity={trackVersion}
        entityType={ENTITY_TYPE.TRACK_VERSION}
        generateUrl={(entity: Version) => {
          const spaceId = row.viewingSpaceId ?? entity.track.spaceId;
          return spaceId
            ? `/space/${spaceId}/${entity.track.id}/${entity.id}`
            : undefined;
        }}
      />
    );
  }

  return null;
};
